import { axios } from '@Api/axiosClient';
import { DocumentDownloadType } from '@Enums/documentDownloadType.enum';
import { RequestStatusType } from '@Enums/requestStatusType.enum';
import type { DocumentSigningStatusDto } from '@Types/documentSigningStatus.type';
import type { AxiosResponse } from 'axios';

const baseUrl: string = 'api/Recipient';
const documentBaseUrl: string = 'api/Document';
const documentSigningRequestBaseUrl: string = 'api/DocumentSigningRequest';

/** Legacy numeric (0-9) and string values from API → RequestStatusType. Used in API layer only. */
const legacyRequestStatusMap: Record<string | number, RequestStatusType> = {
    Draft: RequestStatusType.Draft,
    Canceled: RequestStatusType.Canceled,
    Voided: RequestStatusType.ClosedIncomplete,
    Declined: RequestStatusType.Rejected,
    Sent: RequestStatusType.Sent,
    WaitingForOthers: RequestStatusType.InProgress,
    Started: RequestStatusType.Started,
    Complete: RequestStatusType.Success,
    ManualReview: RequestStatusType.UnderReview,
    Caution: RequestStatusType.Retry,
    HighRisk: RequestStatusType.Blocked,
    0: RequestStatusType.Draft,
    1: RequestStatusType.Canceled,
    2: RequestStatusType.Rejected,
    3: RequestStatusType.Sent,
    4: RequestStatusType.InProgress,
    5: RequestStatusType.Started,
    6: RequestStatusType.Success,
    7: RequestStatusType.UnderReview,
    8: RequestStatusType.Retry,
    9: RequestStatusType.Blocked,
};

function mapLegacyRequestStatus(value: string | number): RequestStatusType {
    if (typeof value === 'number' && value in RequestStatusType && value >= 100) {
        return value as RequestStatusType;
    }
    return legacyRequestStatusMap[value] ?? (value as RequestStatusType);
}

export const getRecipientViewUrlApi = async (): Promise<string> => {
    const response = await axios.get(`${baseUrl}/GetRecipientViewUrl`);
    return response ? response.data : '';
};

export const downloadDocumentApi = async (documentType: DocumentDownloadType): Promise<Blob> => {
    const response = await axios.get(`${documentBaseUrl}/DownloadDocument`, {
        params: { documentType },
        responseType: 'blob'
    });

    return new Blob([response.data], { type: 'application/pdf' });
};

export const markRecipientAsStartedApi = async (recipientToken: string): Promise<AxiosResponse> => {
    return await axios.post(`${baseUrl}/MarkRecipientAsStarted`, { recipientToken });
};

/**
 * Notifies the BFF that the participant finished signing so the associated deal task
 * can be marked completed. Fire-and-forget: failures are logged but do not block UX.
 */
export const finalizeSigningApi = async (): Promise<void> => {
    try {
        await axios.post(`${documentSigningRequestBaseUrl}/FinalizeSigning`);
    } catch (error) {
        console.warn('finalizeSigningApi failed — deal task will remain unchanged', error);
    }
};

export const getRecipientAndDocumentStatusByRecipientTokenApi = async (recipientToken: string): Promise<DocumentSigningStatusDto> => {
    const response = await axios.get(`${baseUrl}/GetRecipientAndDocumentStatusByRecipientToken`, {
        params: { recipientToken }
    });
    const raw = response.data as {
        documentSigningRequestStatusType?: string | number;
        statusCode?: string | number;
        recipientStatusType: unknown;
    };
    const requestStatusRaw = raw.documentSigningRequestStatusType ?? raw.statusCode;
    return {
        ...response.data,
        documentSigningRequestStatusType: requestStatusRaw !== undefined && requestStatusRaw !== null
            ? mapLegacyRequestStatus(requestStatusRaw)
            : undefined,
    };
};
